import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Clock, Coins, Flame, Gem, TrendingUp, Zap } from 'lucide-react';
import { StonApiClient } from '@ston-fi/api';
import {
  CONTRACT_PARAMS,
  LIQUIDITY_ENABLED,
  NATIVE_COIN_SYMBOL,
  SWAP_ENABLED,
  SWAP_STON_POOL,
} from '../ton/config';
import { createLiquidityPoolSnapshot } from '../ton/liquidity';
import { ExplorerLink } from './ExplorerLink';
import { VerifiedBadge } from './VerifiedBadge';
import { formatDateTime, formatPrmd } from '../lib/format';

const MARKET_ENABLED = SWAP_ENABLED || LIQUIDITY_ENABLED;

interface MarketData {
  priceGram: string;
  priceUsd: string | null;
  tvlUsd: string | null;
}

function formatSignificant(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return '—';
  return Number(value.toPrecision(3)).toString();
}

function formatUsd(value: number): string {
  if (!Number.isFinite(value) || value < 0) return '—';
  const digits = value >= 1 ? 2 : 4;
  return `$${value.toLocaleString('en-US', { maximumFractionDigits: digits })}`;
}

/** Live GRAM/PRMD market stats from the STON.fi pool; best-effort, never throws. */
function useMarketData(): MarketData | null {
  const [market, setMarket] = useState<MarketData | null>(null);
  useEffect(() => {
    if (!MARKET_ENABLED) return;
    let cancelled = false;
    void new StonApiClient()
      .getPool(SWAP_STON_POOL)
      .then((info) => {
        if (cancelled) return;
        const pool = createLiquidityPoolSnapshot(info);
        const reservePrmd = Number(pool.reservePrmd);
        const priceGram = Number(pool.reserveTon) / reservePrmd;
        const tvlUsd = info.lpTotalSupplyUsd ? Number(info.lpTotalSupplyUsd) : null;
        const priceUsd =
          tvlUsd !== null && reservePrmd > 0
            ? (tvlUsd / 2) / (reservePrmd / 10 ** CONTRACT_PARAMS.jettonDecimals)
            : null;
        setMarket({
          priceGram: formatSignificant(priceGram),
          priceUsd: priceUsd !== null ? formatUsd(priceUsd) : null,
          tvlUsd: tvlUsd !== null ? formatUsd(tvlUsd) : null,
        });
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);
  return market;
}

interface TokenMarketCardProps {
  remaining: number;
  mintable: boolean;
  lastMintTime: number;
  jettonMasterAddress: string | null;
  firstLoad: boolean;
}

/**
 * Unified token sidebar card: live GRAM/PRMD market stats (price, volume,
 * pool liquidity) alongside the emission and contract details.
 */
export function TokenMarketCard({
  remaining,
  mintable,
  lastMintTime,
  jettonMasterAddress,
  firstLoad,
}: TokenMarketCardProps) {
  const { t } = useTranslation();
  const market = useMarketData();
  const stat = (node: ReactNode) =>
    firstLoad ? <span className="skeleton skeleton--value" /> : node;

  const row = (icon: ReactNode, label: string, value: ReactNode) => (
    <div className="market-row">
      <span className="detail-label">
        {icon} {label}
      </span>
      <span className="market-row__value">{value}</span>
    </div>
  );

  return (
    <div className="token-info-card">
      <h3 className="card-head">
        <TrendingUp size={18} aria-hidden="true" /> {t('jetton.market')}
      </h3>

      {MARKET_ENABLED && (
        <>
          {row(
            <Coins size={12} aria-hidden="true" />,
            t('jetton.price'),
            market ? (
              <>
                {market.priceGram} {NATIVE_COIN_SYMBOL}
                {market.priceUsd ? ` · ≈${market.priceUsd}` : ''}
              </>
            ) : (
              <span className="skeleton skeleton--value" />
            ),
          )}
          {row(
            <TrendingUp size={12} aria-hidden="true" />,
            t('jetton.tvl'),
            market ? (market.tvlUsd ?? '—') : <span className="skeleton skeleton--value" />,
          )}
        </>
      )}

      {row(
        <Flame size={12} aria-hidden="true" />,
        t('jetton.remaining'),
        stat(formatPrmd(remaining)),
      )}
      {row(
        <Zap size={12} aria-hidden="true" />,
        t('jetton.emission'),
        stat(mintable ? t('jetton.emissionActive') : t('jetton.emissionDone')),
      )}
      {row(
        <Clock size={12} aria-hidden="true" />,
        t('jetton.lastMint'),
        formatDateTime(lastMintTime),
      )}
      <div className="market-row">
        <span className="detail-label">
          <Gem size={12} aria-hidden="true" /> {t('jetton.jettonMaster')}
        </span>
        <span className="market-row__value">
          <ExplorerLink address={jettonMasterAddress} />
        </span>
      </div>
      <div className="verified-badges">
        <VerifiedBadge address={jettonMasterAddress} />
      </div>

      {MARKET_ENABLED && <p className="market-source">{t('jetton.marketSource')}</p>}
    </div>
  );
}
